import { UserRole } from '@gooduncles/gu-app-schema';
import { Modal } from 'antd';
import { FC, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { isVersionLessThan } from 'src/utils/common';
import { observeCommerceConf } from 'src/utils/firebase/firebase-short-cut';

import { useCommerceConf } from './redux/hooks/useCommerceConf';

import ChatRoomPopupPage from './pages/ChatRoomPopupPage';
import SigninPage from './pages/SigninPage';

import Loading from './components/Loading/Loading';

import { version } from './constants/firebase.constants';
import AppLayout from './layout/AppLayout';
import { getSiteMap } from './routes';
import { useAuth } from './stores/auth-context';

const siteMap = getSiteMap();

const RouteElements = (role?: UserRole) => {
  if (role === undefined) return null;
  return (
    <>
      <Route path='/login' element={<Navigate to={'/'} replace />} />
      {Object.entries(siteMap)
        .filter(([, value]) => value.icon !== undefined && (value.role === undefined || value.role.includes(role)))
        .map(([path, site]) => (
          <Route key={path} path={`/${path}/*`} element={<site.page title={site.title} />} />
        ))}
    </>
  );
};

const Router: FC = () => {
  const { authUser, authLoading, isLoggedIn, user } = useAuth();
  const location = useLocation();
  const [initialPath, setInitialPath] = useState<string | null>(null);
  const initialized = authUser !== null;
  const { onSetCommerceConf } = useCommerceConf();
  console.log('initialized', initialized);

  // 최초 동작시 2번의 render를 진행한다.
  // 1. loading true render
  // 2. loading false render
  useEffect(() => {
    if (!initialized && initialPath === null) {
      // 최초 초기화가 안된 상태에서 접근한 주소를 기억해야한다.
      // 무한 루프를 막기위해 로그인 페이지는 제외한다.
      if (!['/', '/login'].includes(location.pathname)) {
        const path = location.pathname.split('/')[1];
        if (siteMap[path]) {
          setInitialPath(location.pathname + location.search);
        }
      }
    }
  }, [initialPath, initialized, location.pathname]);

  useEffect(() => {
    const subscription = observeCommerceConf().subscribe((commerceConf) => {
      onSetCommerceConf(commerceConf ?? null);
      console.log('commerceConf', commerceConf);

      // 버전 체크: minConsoleVersion보다 현재 버전이 낮으면 강제 업데이트 팝업 표시
      if (commerceConf?.minConsoleVersion && isVersionLessThan(version, commerceConf.minConsoleVersion)) {
        Modal.warning({
          title: '업데이트가 필요합니다',
          content: (
            <div>
              <p>새로운 버전이 배포되었습니다.</p>
              <p>페이지를 새로고침하여 최신 버전으로 업데이트해주세요.</p>
              <p style={{ marginTop: 8, color: '#8c8c8c', fontSize: 12 }}>
                현재 버전: {version}
                <br />
                최소 요구 버전: {commerceConf.minConsoleVersion}
              </p>
            </div>
          ),
          okText: '새로고침',
          closable: false,
          maskClosable: false,
          keyboard: false,
          onOk: () => {
            window.location.reload();
          },
        });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [onSetCommerceConf]);

  return (
    <>
      {authLoading && <Loading title='잠시만 기다려주세요.' />}
      <Routes>
        {/* 채팅 팝업: AppLayout(네비게이션) 없이 단독 창으로 표시 */}
        <Route path='/chat-popup' element={<ChatRoomPopupPage />} />

        <Route element={<AppLayout />}>
          {/* 로그인 상태에 따른 리다이렉트 */}
          <Route path='/' element={<Navigate to={isLoggedIn ? initialPath ?? '/dashboard' : '/login'} replace />} />
          {!isLoggedIn && <Route path='/login' element={initialized ? <SigninPage /> : null} />}
          {/* 그 외 로그인이 필요한 페이지들 */}
          {isLoggedIn && RouteElements(user?.role)}
        </Route>

        {/* 기본 루트 경로로 리다이렉트 */}
        <Route path='*' element={<Navigate to='/' replace />} />
      </Routes>
    </>
  );
};

export default Router;
